// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import { addMonths, addYears, isSameMonth, isSameYear, startOfMonth, startOfYear } from 'date-fns';

import { parseDate } from '../../internal/utils/date-time';
import { DateRangePickerProps } from '../interfaces';
import { RangeCalendarI18nStrings } from './interfaces';

export function findDateToFocus(
  selected: Date | null,
  baseDate: Date,
  isDateEnabled: DateRangePickerProps.IsDateEnabledFunction
) {
  if (selected && isDateEnabled(selected) && isSameMonth(selected, baseDate)) {
    return selected;
  }
  const today = new Date();
  if (isDateEnabled(today) && isSameMonth(today, baseDate)) {
    return today;
  }
  if (isDateEnabled(baseDate)) {
    return baseDate;
  }
  return null;
}

export function findMonthToFocus(
  selected: Date | null,
  baseDate: Date,
  isMonthEnabled: DateRangePickerProps.IsDateEnabledFunction
) {
  if (selected && isMonthEnabled(selected) && isSameYear(selected, baseDate)) {
    return selected;
  }

  const today = new Date();
  if (isMonthEnabled(today) && isSameYear(today, baseDate)) {
    return today;
  }
  if (isMonthEnabled(baseDate)) {
    return baseDate;
  }
  return null;
}

export function findMonthToDisplay(value: DateRangePickerProps.PendingAbsoluteValue, isSingleGrid: boolean) {
  if (value.start.date) {
    const startDate = parseDate(value.start.date);
    if (isSingleGrid) {
      return startOfMonth(startDate);
    }
    return startOfMonth(addMonths(startDate, 1));
  }
  if (value.end.date) {
    return startOfMonth(parseDate(value.end.date));
  }
  return startOfMonth(Date.now());
}

export function findYearToDisplay(value: DateRangePickerProps.PendingAbsoluteValue, isSingleGrid: boolean) {
  if (value.start.date) {
    const startDate = parseDate(value.start.date);
    if (isSingleGrid) {
      return startOfYear(startDate);
    }
    return startOfYear(addYears(startDate, 1));
  }
  if (value.end.date) {
    return startOfYear(parseDate(value.end.date));
  }
  return startOfYear(Date.now());
}

export const generateI18NFallbackKey = (isMonthPicker: boolean, isDateOnly: boolean) => {
  if (isMonthPicker) {
    return 'i18nStrings.monthConstraintText';
  }
  if (isDateOnly) {
    return 'i18nStrings.dateConstraintText';
  }
  return 'i18nStrings.dateTimeConstraintText';
};

export const generateI18NKey = (isMonthPicker: boolean, isDateOnly: boolean, isIso: boolean) => {
  if (isMonthPicker) {
    return isIso ? 'i18nStrings.isoMonthConstraintText' : 'i18nStrings.slashedMonthConstraintText';
  }
  if (isDateOnly) {
    return isIso ? 'i18nStrings.isoDateConstraintText' : 'i18nStrings.slashedDateConstraintText';
  }
  return isIso ? 'i18nStrings.isoDateTimeConstraintText' : 'i18nStrings.slashedDateTimeConstraintText';
};

export const provideI18N = (
  i18nStrings: RangeCalendarI18nStrings,
  isMonthPicker: boolean,
  isDateOnly: boolean,
  isIso: boolean
): undefined | string => {
  let result;
  if (isMonthPicker) {
    result = isIso ? i18nStrings?.isoMonthConstraintText : i18nStrings?.slashedMonthConstraintText;
    if (!result) {
      result = i18nStrings?.monthConstraintText;
    }
  } else if (isDateOnly) {
    result = isIso ? i18nStrings?.isoDateConstraintText : i18nStrings?.slashedDateConstraintText;
    if (!result) {
      result = i18nStrings?.dateConstraintText;
    }
  }
  if (!result) {
    result = isIso ? i18nStrings?.isoDateTimeConstraintText : i18nStrings?.slashedDateTimeConstraintText;
    if (!result) {
      result = i18nStrings?.dateTimeConstraintText;
    }
  }
  return result;
};



// WEBPACK FOOTER //
// /Volumes/src/date-range-picker/calendar/utils.ts